// Query Hooks
// Contains all query hooks for data fetching operations

import React from 'react'
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  queryOptions
} from '@tanstack/react-query'
import { QUERY_KEYS } from '../queryKeys'
import { ApiResponse, QueryOptions } from '../types'
import { processHandlers } from '../processHandlers'
import { getDemographicAnswers } from '../processHandlers/demographics'
import useAppState, { State } from '../../hooks/useAppState'
import type { ReportFlowEnrichedSession } from '@/features/facilitator/facilitatorReportFlow/types'
import { Language } from '../../../../pure-js/types/ExpertVideo'
import { BlogPostType } from '../../../../pure-js/types/BlogPost'
import type { GetFacilitatorLiveSessionMonitorResponse } from '../../../../pure-js/types/FacilitatorLiveSessionMonitor'
import type { GetReflectionsRequest } from '../../../../pure-js/types/Reflection'
import type { SprintActivityWherebyMeetingRequest } from '../../../../pure-js/types/WherebyTypes'
import { isPlatformSuperAdmin } from '../../../../pure-js/libs/isSuperUser'

// User Queries
export const useUsers = (filters?: Record<string, string | number | boolean>, options?: QueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.USERS, filters],
    queryFn: () => processHandlers.getUsers(filters),
    ...options
  })
}

export const useUser = (id: string, options?: QueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.USER_DETAIL, id],
    queryFn: () => processHandlers.getUser(id),
    enabled: !!id,
    ...options
  })
}

export const useCurrentUser = (options?: QueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.CURRENT_USER],
    queryFn: () => processHandlers.getCurrentUser(),
    ...options
  })
}

export const useUserProfile = (options?: QueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.USER_PROFILE],
    queryFn: () => processHandlers.getUserProfile(),
    ...options
  })
}

export const useUserProfileReport = (request?: { userId?: string }, options?: QueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.USER_PROFILE_REPORT, request?.userId],
    queryFn: () => processHandlers.getUserProfileReport(request),
    ...options
  })
}

export const useDemographicAnswers = (userId: string, options?: QueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.USER_DEMOGRAPHICS, userId],
    queryFn: () => getDemographicAnswers({ userId }),
    enabled: !!userId,
    ...options
  })
}

// Session Queries
export const useSessions = (filters?: Record<string, string | number | boolean>, options?: QueryOptions) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId

  return useQuery({
    queryKey: [QUERY_KEYS.SESSIONS, filters, queryOrgId],
    queryFn: () => processHandlers.getSessions(filters),
    ...options
  })
}

export const useSession = (id: string, options?: QueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.SESSION_DETAIL, id],
    queryFn: () => processHandlers.getSession(id),
    enabled: !!id,
    ...options
  })
}

// Enhanced Team Queries
export const useTeams = (filters?: Record<string, string | number | boolean>, options?: QueryOptions) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId

  return useQuery({
    queryKey: [QUERY_KEYS.TEAMS, filters, queryOrgId],
    queryFn: () => processHandlers.getTeams(filters),
    ...options
  })
}

export const useTeam = (id: string, options?: QueryOptions) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId
  const isEnabled = options?.enabled !== undefined ? options.enabled : !!id

  if (isEnabled && (!id || id.trim() === '' || id === 'undefined' || id === 'null')) {
    console.error('❌ [useTeam] Query enabled but ID is invalid:', id)
  }

  return useQuery({
    queryKey: QUERY_KEYS.TEAM_DETAIL(queryOrgId, id),
    queryFn: () => {
      return processHandlers.getTeam(id)
    },
    enabled: isEnabled,
    ...options
  })
}

export const useTeamsByIds = (ids: string[], options?: QueryOptions) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId

  return useQuery({
    queryKey: [QUERY_KEYS.TEAMS_BY_IDS, ids, queryOrgId],
    queryFn: () => processHandlers.getTeamsByIds(ids),
    enabled: ids && ids.length > 0,
    ...options
  })
}

export const useTeamsByMember = (memberId: string, options?: QueryOptions) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId
  return useQuery({
    queryKey: QUERY_KEYS.TEAMS_BY_MEMBER(queryOrgId, memberId),
    queryFn: () => processHandlers.getTeamsByMember(memberId),
    enabled: !!memberId,
    ...options
  })
}

export const useActiveTeams = (options?: QueryOptions) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId

  return useQuery({
    queryKey: [QUERY_KEYS.ACTIVE_TEAMS, queryOrgId],
    queryFn: () => processHandlers.getActiveTeams(),
    ...options
  })
}

export const useTeamStats = (teamId: string, options?: QueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.TEAM_STATS, teamId],
    queryFn: () => processHandlers.getTeamStats(teamId),
    ...options
  })
}

// Whereby Queries
const WHEREBY_ROOM_STALE_TIME_MS = 15_000 // 15 s
// The active activity-scoped room from the whereby_meetings table. Modest staleTime so passive
// remounts/refocus don't refetch storm; convergence is guaranteed server-side by the room claim/lease
// (concurrent creates can't produce two rooms) and by explicit refetches during call preparation.
export const useActivityWherebyMeeting = (
  params: { teamId?: string; activityId?: string } | undefined,
  options?: QueryOptions
) => {
  const { state } = useAppState()
  const { teamId, activityId } = params ?? {}

  return useQuery({
    queryKey: [QUERY_KEYS.ACTIVITY_WHEREBY_MEETING, teamId, activityId],
    queryFn: () =>
      processHandlers.getActivityWherebyMeeting({ teamId: teamId ?? '', activityId: activityId ?? '' }, state),
    enabled: Boolean(teamId && activityId),
    staleTime: WHEREBY_ROOM_STALE_TIME_MS,
    ...options
  })
}

// The active room a whole sprint holds one activity in; same staleTime reasoning as useActivityWherebyMeeting.
export const useSprintActivityWherebyMeeting = (
  params: SprintActivityWherebyMeetingRequest | undefined,
  options?: QueryOptions
) => {
  const { state } = useAppState()
  const { sprintId, activityId } = params ?? {}

  return useQuery({
    queryKey: [QUERY_KEYS.SPRINT_ACTIVITY_WHEREBY_MEETING, sprintId, activityId],
    queryFn: () =>
      processHandlers.getSprintActivityWherebyMeeting(
        { sprintId: sprintId ?? '', activityId: activityId ?? '' },
        state
      ),
    enabled: Boolean(sprintId && activityId),
    staleTime: WHEREBY_ROOM_STALE_TIME_MS,
    ...options
  })
}

// The team's active room from the whereby_meetings table. Modest staleTime (see useActivityWherebyMeeting):
// the backend claim/lease prevents competing rooms, so we don't need to refetch on every mount/focus.
export const useTeamWherebyMeeting = (teamId: string | undefined, options?: QueryOptions) => {
  const { state } = useAppState()

  return useQuery({
    queryKey: [QUERY_KEYS.TEAM_WHEREBY_MEETING, teamId],
    queryFn: () => processHandlers.getTeamWherebyMeeting(teamId ?? '', state),
    enabled: Boolean(teamId),
    staleTime: WHEREBY_ROOM_STALE_TIME_MS,
    ...options
  })
}

// Live room presence changes second-by-second; keep it fresh (staleTime 0, short refetch interval).
export const useLiveWherebyRooms = (options?: QueryOptions) => {
  const { state } = useAppState()

  return useQuery({
    queryKey: [QUERY_KEYS.LIVE_WHEREBY_ROOMS],
    queryFn: () => processHandlers.getLiveWherebyRooms(state),
    staleTime: 0,
    refetchInterval: 5000,
    ...options
  })
}

// Report Queries
export const useReports = (filters?: Record<string, string | number | boolean>, options?: QueryOptions) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId

  return useQuery({
    queryKey: [QUERY_KEYS.REPORTS, filters, queryOrgId],
    queryFn: () => processHandlers.getReports(filters, state),
    ...options
  })
}

export const useReport = (id: string, options?: QueryOptions) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId
  const isEnabled = options?.enabled !== undefined ? options.enabled : !!id

  if (isEnabled && (!id || id.trim() === '' || id === 'undefined' || id === 'null')) {
    console.error('❌ [useReport] Query enabled but ID is invalid:', id)
  }

  return useQuery({
    queryKey: QUERY_KEYS.REPORT_DETAIL(queryOrgId, id),
    queryFn: () => {
      return processHandlers.getReport(id, state)
    },
    enabled: isEnabled,
    ...options
  })
}

export const useReportsBySession = (sessionId: string, options?: QueryOptions) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId

  return useQuery({
    queryKey: [QUERY_KEYS.REPORTS_BY_SESSION, sessionId, queryOrgId],
    queryFn: () => processHandlers.getReportsBySession(sessionId),
    enabled: !!sessionId,
    ...options
  })
}

export const useReportsByTeam = (teamId: string, options?: QueryOptions) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId

  return useQuery({
    queryKey: [QUERY_KEYS.REPORTS_BY_TEAM, teamId, queryOrgId],
    queryFn: () => processHandlers.getReportsByTeam(teamId),
    enabled: !!teamId,
    ...options
  })
}

export const useReportsByUser = (userId: string, options?: QueryOptions) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId

  return useQuery({
    queryKey: [QUERY_KEYS.REPORTS_BY_USER, userId, queryOrgId],
    queryFn: () => processHandlers.getReportsByUser(userId),
    enabled: !!userId,
    ...options
  })
}

export const useGeneratedReports = (options?: QueryOptions) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId

  return useQuery({
    queryKey: [QUERY_KEYS.GENERATED_REPORTS, queryOrgId],
    queryFn: () => processHandlers.getGeneratedReports(),
    ...options
  })
}

// Dashboard Queries
export const useFacilitatorDashboard = (options?: QueryOptions) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId

  return useQuery({
    queryKey: [QUERY_KEYS.FACILITATOR_DASHBOARD, queryOrgId],
    queryFn: () => processHandlers.getFacilitatorDashboard(),
    ...options
  })
}

// Training Sprint Queries
export const useTrainingSprints = (filters?: Record<string, string | number | boolean>, options?: QueryOptions) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId

  return useQuery({
    queryKey: [QUERY_KEYS.TRAINING_SPRINTS, filters, queryOrgId],
    queryFn: () => processHandlers.getTrainingSprints(filters, state),
    ...options
  })
}

const TRAINING_SPRINTS_PAGE_SIZE = 20

// Offset-paged training sprints for infinite scroll; search is applied server-side.
export const useInfiniteTrainingSprints = (search?: string, pageSize: number = TRAINING_SPRINTS_PAGE_SIZE) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId
  const normalizedSearch = search?.trim() || undefined

  return useInfiniteQuery({
    queryKey: [QUERY_KEYS.TRAINING_SPRINTS, 'infinite', normalizedSearch ?? '', queryOrgId, pageSize],
    // Both are sent: the server pages by cursor where it can and by offset otherwise, and the caller
    // does not need to know which.
    queryFn: ({ pageParam }) =>
      processHandlers.getTrainingSprintsPage(state, {
        limit: pageSize,
        offset: pageParam.offset,
        cursor: pageParam.cursor,
        search: normalizedSearch
      }),
    initialPageParam: { offset: 0, cursor: undefined as string | undefined },
    getNextPageParam: (lastPage) =>
      lastPage?.data?.hasMore ? { offset: lastPage.data.nextOffset, cursor: lastPage.data.nextCursor } : undefined,
    // Keep the current list visible while a new search term fetches (inline spinner instead of full-page loader).
    placeholderData: keepPreviousData,
    staleTime: 30_000
  })
}

export const useTrainingSprint = (id: string, options?: QueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.TRAINING_SPRINT_DETAIL, id],
    queryFn: () => processHandlers.getTrainingSprint(id),
    enabled: !!id,
    ...options
  })
}

export const useActiveTrainingSprints = (options?: QueryOptions) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId

  return useQuery({
    queryKey: [QUERY_KEYS.ACTIVE_TRAINING_SPRINTS, queryOrgId],
    queryFn: () => processHandlers.getActiveTrainingSprints(),
    ...options
  })
}

export const useUpcomingTrainingSprints = (options?: QueryOptions) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId

  return useQuery({
    queryKey: [QUERY_KEYS.UPCOMING_TRAINING_SPRINTS, queryOrgId],
    queryFn: () => processHandlers.getUpcomingTrainingSprints(),
    ...options
  })
}

// Firebase Backend Sprint Queries
export const useGetSprint = (id: string, options?: QueryOptions) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId

  const isEnabled = options?.enabled !== undefined ? options.enabled : !!id

  if (isEnabled && (!id || id.trim() === '' || id === 'undefined' || id === 'null')) {
    console.error('❌ [useGetSprint] Query enabled but ID is invalid:', {
      id: id,
      enabled: isEnabled
    })
  }

  return useQuery({
    queryKey: [QUERY_KEYS.SPRINT, id, queryOrgId],
    queryFn: () => {
      return processHandlers.getSprint(id, state)
    },
    enabled: isEnabled,
    ...options
  })
}

/**
 * Resolve specific sprints by id. Callers that already know which sprints they need must use this
 * rather than scanning `useGetSprints`, whose page can legitimately exclude an older sprint.
 */
export const useGetSprintsByIds = (sprintIds: string[], options?: QueryOptions) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId
  const stableIds = [...new Set(sprintIds.filter(Boolean))].sort()

  return useQuery({
    queryKey: [QUERY_KEYS.SPRINTS, 'byIds', queryOrgId, stableIds.join(',')],
    queryFn: () => processHandlers.getSprintsByIds(state, stableIds),
    enabled: stableIds.length > 0,
    ...options
  })
}

// Raw Sprint list for callers that need teams/participants (pickers, report + reflection steps).
// Server-paged and server-searched — never the backend's fetch-and-heal-everything path.
export const useGetSprints = (options?: QueryOptions & { search?: string }) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId
  const search = options?.search?.trim() || undefined

  return useQuery({
    queryKey: [QUERY_KEYS.SPRINTS, queryOrgId, search ?? ''],
    queryFn: () => processHandlers.getSprints(state, { search }),
    placeholderData: (previousData) => previousData,
    ...options
  })
}

// Search Queries
export const useGlobalSearch = (query: string, options?: QueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GLOBAL_SEARCH, query],
    queryFn: () => processHandlers.globalSearch(query),
    enabled: !!query && query.length > 2,
    ...options
  })
}

export const useUserSearch = (query: string, options?: QueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.USER_SEARCH, query],
    queryFn: () => processHandlers.searchUsers(query),
    enabled: !!query && query.length > 2,
    ...options
  })
}

export const useSessionSearch = (query: string, options?: QueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.SESSION_SEARCH, query],
    queryFn: () => processHandlers.searchSessions(query),
    enabled: !!query && query.length > 2,
    ...options
  })
}

// Configuration Queries
export const useAppConfig = (options?: QueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.APP_CONFIG],
    queryFn: () => processHandlers.getAppConfig(),
    ...options
  })
}

export const useUserConfig = (options?: QueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.USER_CONFIG],
    queryFn: () => processHandlers.getUserConfig(),
    ...options
  })
}

// Group Queries
export const useGroups = (filters?: Record<string, string | number | boolean>, options?: QueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GROUPS, filters],
    queryFn: () => processHandlers.getGroups(filters),
    ...options
  })
}

export const useGroup = (id: string, options?: QueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GROUP_DETAIL, id],
    queryFn: () => processHandlers.getGroup(id),
    enabled: !!id,
    ...options
  })
}

export const useGroupsWithTeams = (options?: QueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GROUPS_WITH_TEAMS],
    queryFn: () => processHandlers.getGroupsWithTeams(),
    ...options
  })
}

export const useActiveGroups = (options?: QueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ACTIVE_GROUPS],
    queryFn: () => processHandlers.getActiveGroups(),
    ...options
  })
}

// QueryHooksDeprecated Migration - New TanStack Query Hooks
// These hooks replace the old useFirestoreQuery-based hooks from QueryHooksDeprecated.ts

export const useQuestionnaire = (params: { userId: string; context: string }, options?: QueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.QUESTIONNAIRE, JSON.stringify({ userId: params.userId, context: params.context })],
    queryFn: () => processHandlers.getQuestionnaire(params),
    enabled: !!params.userId && !!params.context,
    ...options
  })
}

export const useQuestionnaireAnswer = (
  params: { questionnaireId: string; questionId: string },
  options?: QueryOptions
) => {
  return useQuery({
    queryKey: [QUERY_KEYS.QUESTIONNAIRE_ANSWER, JSON.stringify(params)],
    queryFn: () => processHandlers.getQuestionnaireAnswer(params),
    enabled: !!params.questionnaireId && !!params.questionId,
    ...options
  })
}

export const useQuestionnaireAnswers = (questionnaireId: string, options?: QueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.QUESTIONNAIRE_ANSWER, 'all', questionnaireId],
    queryFn: () => processHandlers.getQuestionnaireAnswers(questionnaireId),
    enabled: !!questionnaireId,
    ...options
  })
}

// Activity Queries

export const useUpcomingActivities = (options?: QueryOptions) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId

  return useQuery({
    queryKey: [QUERY_KEYS.UPCOMING_ACTIVITIES, queryOrgId],
    queryFn: async () => {
      return processHandlers.getUpcomingActivitiesListForGamer(state)
    },
    placeholderData: (previousData) => previousData, // Keep previous data during refetch to prevent scroll collapse
    ...options
  })
}

export const useFacilitatorLiveSessionMonitor = (options?: QueryOptions & { pollingInterval?: number }) => {
  const { state } = useAppState()
  const pollingInterval = options?.pollingInterval ?? 5_000

  return useQuery<ApiResponse<GetFacilitatorLiveSessionMonitorResponse>>({
    queryKey: [QUERY_KEYS.FACILITATOR_LIVE_SESSION_MONITOR, state.user?.id ?? ''],
    queryFn: () => processHandlers.getFacilitatorLiveSessionMonitor(state),
    enabled: Boolean(state.user?.id),
    refetchInterval: pollingInterval,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    // Always pull fresh when landing on the page so returning from another route never shows stale cards.
    refetchOnMount: 'always',
    staleTime: 0,
    placeholderData: (previousData) => previousData,
    ...options
  })
}

export const useGetUpcomingActivityForGamer = (options?: QueryOptions) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId

  return useQuery({
    queryKey: [QUERY_KEYS.UPCOMING_ACTIVITIES_GAMER, queryOrgId],
    queryFn: async () => {
      try {
        return await processHandlers.getUpcomingActivityForGamer(state)
      } catch (error) {
        // Handle authorization errors gracefully - return null instead of throwing
        const errorObj = error as { code?: string; message?: string }
        if (errorObj?.code === 'not-authorized' || errorObj?.message?.includes('not-authorized')) {
          return { data: null, success: false }
        }
        throw error
      }
    },
    // Fresh so a tab/window switch (refetchOnWindowFocus) re-resolves immediately and two players
    // converge instead of showing different states until a manual refresh.
    staleTime: 0,
    retry: (failureCount, error) => {
      // Don't retry on authorization errors
      const errorObj = error as { code?: string; message?: string }
      if (errorObj?.code === 'not-authorized' || errorObj?.message?.includes('not-authorized')) {
        return false
      }
      // Retry up to 3 times for other errors
      return failureCount < 3
    },
    ...options
  })
}

export const useGetUpcomingActivityForGamerPolling = (options?: QueryOptions) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId

  return useQuery({
    queryKey: [QUERY_KEYS.UPCOMING_ACTIVITIES_GAMER, queryOrgId],
    queryFn: async () => {
      try {
        return await processHandlers.getUpcomingActivityForGamer(state)
      } catch (error) {
        // Handle authorization errors gracefully - return null instead of throwing
        const errorObj = error as { code?: string; message?: string }
        if (errorObj?.code === 'not-authorized' || errorObj?.message?.includes('not-authorized')) {
          return { data: null, success: false }
        }
        throw error
      }
    },
    // Keep polling even when the window is unfocused (side-by-side players) and re-resolve on focus, so
    // both players converge to the same next-activity/room without a manual refresh.
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
    staleTime: 0,
    retry: (failureCount, error) => {
      // Don't retry on authorization errors
      const errorObj = error as { code?: string; message?: string }
      if (errorObj?.code === 'not-authorized' || errorObj?.message?.includes('not-authorized')) {
        return false
      }
      // Retry up to 3 times for other errors
      return failureCount < 3
    },
    ...options
  })
}

// The gamer's activities list: what the dashboard shows, and what the end of a game reads once.
export const upcomingActivitiesListForGamerQuery = (state: State) =>
  queryOptions({
    queryKey: [
      QUERY_KEYS.UPCOMING_ACTIVITIES_GAMER_LIST,
      state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId
    ],
    queryFn: () => processHandlers.getUpcomingActivitiesListForGamer(state),
    // Shorter than the 5-min global default so switching between Dashboard / Upcoming Activities (or
    // refocusing the tab) refetches once the data is >30s old — without polling every few seconds.
    // Rapid back-and-forth within 30s still serves cache. refetchOnMount/onWindowFocus (RQ defaults)
    // then do the actual refetch when stale.
    staleTime: 30_000
  })

export const useGetUpcomingActivitiesListForGamer = (options?: QueryOptions) => {
  const { state } = useAppState()

  return useQuery({ ...upcomingActivitiesListForGamerQuery(state), ...options })
}

const UPCOMING_ACTIVITIES_PAGE_SIZE = 20

// Offset-paged upcoming activities for infinite scroll. Pages accumulate; getNextPageParam stops at hasMore.
export const useInfiniteUpcomingActivitiesForGamer = (pageSize: number = UPCOMING_ACTIVITIES_PAGE_SIZE) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId

  return useInfiniteQuery({
    queryKey: [QUERY_KEYS.UPCOMING_ACTIVITIES_GAMER_LIST, 'infinite', queryOrgId, pageSize],
    queryFn: ({ pageParam }) =>
      processHandlers.getUpcomingActivitiesPageForGamer(state, { limit: pageSize, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => (lastPage?.data?.hasMore ? lastPage.data.nextOffset : undefined),
    staleTime: 30_000
  })
}

// Report Generation Queries

export const useGenerateReportData = (
  request: { sessions: { id: string; teamId: string }[]; reportId?: string; title?: string; sprintIds?: string[] },
  options?: QueryOptions
) => {
  const { state } = useAppState()

  return useQuery({
    queryKey: [QUERY_KEYS.SESSIONS_DATAPOINTS, JSON.stringify(request)],
    queryFn: () => processHandlers.generateReportData(request, state),
    enabled: request.sessions.length > 0,
    ...options
  })
}

// Feedback Queries

/**
 * Get latest session feedback (old format - still used by individual analysis page)
 * @deprecated Use useSwimmerTeamDataForGamer for new implementations
 */
export const useLatestSessionFeedback = (sessionId: string, options?: QueryOptions) => {
  const { state } = useAppState()

  return useQuery({
    queryKey: [QUERY_KEYS.LATEST_SESSION_FEEDBACK, sessionId],
    queryFn: () => processHandlers.getLatestSessionFeedback(state),
    enabled: !!sessionId,
    ...options
  })
}

/**
 * Get latest session feedback (old format - still used by individual analysis page)
 * @deprecated Use  for new implementations
 */
export const useSkimmerDataForGamer = (options?: QueryOptions) => {
  const { state } = useAppState()

  return useQuery({
    queryKey: [QUERY_KEYS.SKIMMER_DATA_FOR_GAMER],
    queryFn: () => processHandlers.getLatestFeedbackDataForGamer(state),
    staleTime: 0, // Feedback changes after each session — always fetch fresh
    ...options
  })
}
/**
 * Get skimmer feedback data for current gamer
 * Returns simplified team and individual feedback insights
 */
export const useLatestFeedbackDataForGamer = (options?: QueryOptions) => {
  const { state } = useAppState()

  return useQuery({
    queryKey: [QUERY_KEYS.SKIMMER_DATA_FOR_GAMER],
    queryFn: () => processHandlers.getLatestFeedbackDataForGamer(state),
    staleTime: 0, // Feedback changes after each session — always fetch fresh
    ...options
  })
}

/**
 * Get swimmer feedback data for current gamer
 * Returns detailed feedback data with full swimmer format including all metrics and visualizations
 */
export const useSwimmerTeamDataForGamer = (options?: QueryOptions) => {
  const { state } = useAppState()

  return useQuery({
    queryKey: [QUERY_KEYS.SWIMMER_TEAM_DATA_FOR_GAMER],
    queryFn: () => processHandlers.getSwimmerTeamDataForGamer(state),
    staleTime: 0, // Feedback changes after each session — always fetch fresh
    ...options
  })
}

/**
 * Get swimmer individual feedback data for current gamer
 * Returns detailed individual feedback data with full swimmer format including all metrics and visualizations
 */
export const useSwimmerIndividualDataForGamer = (options?: QueryOptions) => {
  const { state } = useAppState()

  return useQuery({
    queryKey: [QUERY_KEYS.SWIMMER_INDIVIDUAL_DATA_FOR_GAMER],
    queryFn: () => processHandlers.getSwimmerIndividualDataForGamer(state),
    staleTime: 0, // Feedback changes after each session — always fetch fresh
    ...options
  })
}

/**
 * Get user achievements for current gamer
 * Returns UserAchievements with all achievements, unseen achievements, and totals
 */
export const useGetUserAchievements = (options?: QueryOptions) => {
  const { state } = useAppState()

  return useQuery({
    queryKey: [QUERY_KEYS.USER_ACHIEVEMENTS],
    queryFn: () => processHandlers.getUserAchievements(state),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options
  })
}

/**
 * Mark achievements as seen
 * Mutation to clear unseen achievements from user's achievement document
 */
export const useMarkAchievementsSeen = () => {
  const { state } = useAppState()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (achievementIds?: string[]) => processHandlers.markAchievementsSeen(state, achievementIds),
    onSuccess: () => {
      // Invalidate user achievements to refetch with updated unseen list
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER_ACHIEVEMENTS] })
    }
  })
}

/**
 * Unsee all achievements (DEV/ADMIN only)
 * Repopulates unseen achievements from already-earned achievements
 */
export const useUnseeAllAchievements = () => {
  const { state } = useAppState()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (req?: { userId?: string }) => {
      const response = await processHandlers.unseeAllAchievements(state, req || {})
      // If the response indicates failure, throw an error so onError is called
      if (!response.success) {
        throw new Error(response.error || 'Failed to unsee achievements')
      }
      return response
    },
    onSuccess: () => {
      // Invalidate user achievements to refetch with updated unseen list
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER_ACHIEVEMENTS] })
    }
  })
}

/** While the progress bar is running, checked often enough to feel immediate. */
const FEEDBACK_FAST_POLL_MS = 3000 // 3 s

/** Matches the progress bar's duration — most analyses land well inside it. */
const FEEDBACK_FAST_POLL_WINDOW_MS = 300000 // 5 min

/** After that, kept alive at a rate that costs nothing but still lands without a manual refresh. */
const FEEDBACK_SLOW_POLL_MS = 30000 // 30 s

/** Everything a finished analysis rewrites — the collaboration profile holds it too, not just the three views. */
const FEEDBACK_DEPENDENT_QUERY_KEYS = [
  QUERY_KEYS.SKIMMER_DATA_FOR_GAMER,
  QUERY_KEYS.SWIMMER_TEAM_DATA_FOR_GAMER,
  QUERY_KEYS.SWIMMER_INDIVIDUAL_DATA_FOR_GAMER,
  QUERY_KEYS.USER_PROFILE_REPORT
]

/** Poll feedback readiness until it is ready or errored: fast while the progress bar runs, slow
 * afterwards, never stopping — one analysis running longer than the team's is ordinary. */
export const useFeedbackReadiness = (options?: QueryOptions) => {
  const { state } = useAppState()
  const queryClient = useQueryClient()
  const pollingStartTimeRef = React.useRef<number | null>(null)
  const hasInvalidatedRef = React.useRef(false)

  return useQuery({
    queryKey: [QUERY_KEYS.FEEDBACK_READINESS],
    queryFn: () => processHandlers.checkFeedbackReadiness(state),
    refetchInterval: (query) => {
      // Initialize polling window start time when the query actually starts fetching.
      // This matters when the query is mounted but disabled for some time.
      if (pollingStartTimeRef.current === null && query.state.fetchStatus === 'fetching') {
        pollingStartTimeRef.current = Date.now()
      }

      const data = query.state.data
      // Stop if feedback is ready or has error
      if (data?.data?.isReady || data?.data?.error) {
        // When feedback becomes ready, invalidate all feedback data queries so they
        // refetch immediately instead of waiting for staleTime to expire (#179)
        if (data?.data?.isReady && !hasInvalidatedRef.current) {
          hasInvalidatedRef.current = true
          FEEDBACK_DEPENDENT_QUERY_KEYS.forEach((queryKey) => queryClient.invalidateQueries({ queryKey: [queryKey] }))
        }
        return false
      }
      // Not ready yet, so the next check is still owed — slowing down past the progress bar's window
      // rather than stopping, which would leave a long-running analysis on an empty page.
      const pollingStartTime = pollingStartTimeRef.current ?? Date.now()
      const elapsed = Date.now() - pollingStartTime
      return elapsed < FEEDBACK_FAST_POLL_WINDOW_MS ? FEEDBACK_FAST_POLL_MS : FEEDBACK_SLOW_POLL_MS
    },
    ...options
  })
}

// Report Generation Queries

export const useSessionsForSprintAndTeam = (sprintIds: string[], teamIds: string[], options?: QueryOptions) => {
  const { state } = useAppState()

  return useQuery<{ data: ReportFlowEnrichedSession[]; success: boolean; error?: string }>({
    queryKey: [QUERY_KEYS.SESSIONS_FOR_SPRINT_TEAM, sprintIds, teamIds],
    queryFn: () => processHandlers.getSessionsForSprintAndTeam(sprintIds, teamIds, state),
    enabled: sprintIds.length > 0 && teamIds.length > 0,
    ...options
  })
}

// AI Coach Queries

/**
 * Get the latest eligible AI Coach session
 * Supports polling with configurable interval (default: 5 seconds)
 * @param options.sessionId - Optional session ID to fetch a specific session
 */
export const useAICoachSession = (options?: QueryOptions & { pollingInterval?: number; sessionId?: string }) => {
  const { state } = useAppState()
  const { sessionId } = options || {}
  const pollingInterval = options?.pollingInterval ?? 5000

  return useQuery({
    queryKey: [QUERY_KEYS.AI_COACH_SESSION, state.user?.id ?? '', sessionId ?? ''],
    queryFn: () => processHandlers.getAICoachSession(state, sessionId),
    enabled: Boolean(state.user?.id),
    refetchInterval: pollingInterval,
    staleTime: 0, // AI Coach session state changes frequently — always treat as stale
    ...options
  })
}

// Video Queries

/**
 * Get expert videos
 * Optionally filter by language (backend handles SV→EN fallback automatically)
 */
/**
 * Get expert videos
 * Fetches all videos without language filtering (filtering is done client-side)
 */
export const useExpertVideos = (options?: QueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.EXPERT_VIDEOS],
    queryFn: () => processHandlers.getExpertVideos({}),
    ...options
  })
}

/**
 * Get tutorial videos
 * Fetches all videos without language filtering (filtering is done client-side)
 */
export const useTutorialVideos = (options?: QueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.TUTORIAL_VIDEOS],
    queryFn: () => processHandlers.getTutorialVideos({}),
    ...options
  })
}

// Blog Post Queries

/**
 * Get blog posts
 * Optionally filter by language and type
 * Backend handles priority sorting and SV→EN fallback
 */
export const useBlogPosts = (request?: { language?: Language; type?: BlogPostType }, options?: QueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.BLOG_POSTS, request],
    queryFn: () => processHandlers.getBlogPosts(request),
    ...options
  })
}

/**
 * Get quotes (e.g. for landing page; use activeOnly: true for active only)
 */
export const useQuotes = (request?: { activeOnly?: boolean }, options?: QueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.QUOTES, request?.activeOnly],
    queryFn: () => processHandlers.getQuotes(request ?? {}),
    ...options
  })
}

/**
 * Get whether the quotes section is enabled on the landing page.
 * When false, the entire quotes slider should be hidden.
 */
export const useLandingQuotesSectionEnabled = (options?: QueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.LANDING_QUOTES_SECTION_ENABLED],
    queryFn: () => processHandlers.getLandingQuotesSectionEnabled(),
    ...options
  })
}

// Sprint Wizard Config Queries

/**
 * Get all active sprint purpose configs from Firestore.
 * Returns an empty array when using mocks (triggers fallback to hardcoded constants in the wizard).
 */
export const useSprintPurposeConfigs = (options?: QueryOptions) =>
  useQuery({
    queryKey: [QUERY_KEYS.SPRINT_PURPOSE_CONFIGS],
    queryFn: () => processHandlers.getSprintPurposeConfigs(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options
  })

// Reflection Queries

export const useReflections = (filters?: GetReflectionsRequest, options?: QueryOptions) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId

  return useQuery({
    queryKey: QUERY_KEYS.REFLECTIONS(queryOrgId, filters?.status),
    queryFn: () => processHandlers.getReflections(filters ?? {}),
    staleTime: 0,
    ...options
  })
}

export const useReflection = (reflectionId: string, options?: QueryOptions) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId

  return useQuery({
    queryKey: QUERY_KEYS.REFLECTION_DETAIL(queryOrgId, reflectionId),
    queryFn: () => processHandlers.getReflection(reflectionId),
    enabled: !!reflectionId,
    staleTime: 0,
    ...options
  })
}

export const useReflectionScopeData = (options?: QueryOptions) => {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId
  const stateRef = React.useRef(state)
  stateRef.current = state
  return useQuery({
    queryKey: [QUERY_KEYS.REFLECTION_SCOPE_DATA, queryOrgId],
    queryFn: () => processHandlers.getReflectionScopeData(stateRef.current),
    staleTime: 5 * 60 * 1000,
    ...options
  })
}

/**
 * Get all active activity type configs from Firestore.
 * Returns an empty array when using mocks (triggers fallback to hardcoded constants in the wizard).
 */
export const useSprintActivityTypeConfigs = (options?: QueryOptions) =>
  useQuery({
    queryKey: [QUERY_KEYS.SPRINT_ACTIVITY_TYPE_CONFIGS],
    queryFn: () => processHandlers.getSprintActivityTypeConfigs(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options
  })

// Organization queries (multi-tenant)

/** The directory of organizations. */
export const useOrganizationsDirectory = (options?: QueryOptions) => {
  const { state } = useAppState()
  const isSuper = isPlatformSuperAdmin(state.user)

  return useQuery({
    queryKey: [QUERY_KEYS.ORGANIZATIONS_DIRECTORY, state.user?.id ?? ''],
    queryFn: () => processHandlers.getOrganizationsDirectory(state),
    enabled: Boolean(state.user?.id) && isSuper,
    staleTime: 60_000,
    ...options
  })
}

export const useOrganizationDetail = (organizationId: string | undefined, options?: QueryOptions) => {
  const { state } = useAppState()

  return useQuery({
    queryKey: QUERY_KEYS.ORGANIZATION_DETAIL(organizationId ?? ''),
    queryFn: () => processHandlers.getOrganization(state, { organizationId }),
    enabled: Boolean(organizationId) && Boolean(state.user?.id),
    staleTime: 5 * 60 * 1000,
    ...options
  })
}

export const useOrgMembers = (organizationId: string | undefined, options?: QueryOptions) => {
  const { state } = useAppState()

  return useQuery({
    queryKey: QUERY_KEYS.ORG_MEMBERS(organizationId ?? ''),
    queryFn: () => processHandlers.getOrgMembers(state, organizationId ?? ''),
    enabled: Boolean(organizationId) && Boolean(state.user?.id),
    staleTime: 0,
    ...options
  })
}

export const useOrgInvites = (organizationId: string | undefined, options?: QueryOptions) => {
  const { state } = useAppState()

  return useQuery({
    queryKey: QUERY_KEYS.ORGANIZATION_INVITES(organizationId ?? ''),
    queryFn: () => processHandlers.getOrgInvites(state, organizationId ?? ''),
    enabled: Boolean(organizationId) && Boolean(state.user?.id),
    staleTime: 0,
    ...options
  })
}
